import { useLocation, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Copy, Mail, Key, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

export default function HospitalRegisterSuccessPage() {
  const location = useLocation();
  const { accessCode, email, healthSystem } = location.state || {};

  // Redirect if no state (direct access)
  if (!accessCode) {
    return <Navigate to="/hospital/register" replace />;
  }

  const copyAccessCode = () => {
    navigator.clipboard.writeText(accessCode);
    toast.success("Access code copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto" />
          </Link>
        </div>

        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-emerald-500 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white">Registration Complete!</h1>
            <p className="text-emerald-100 mt-2">Your account has been created successfully</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Health System Info */}
            <div className="text-center">
              <p className="text-slate-600">You're now registered with</p>
              <p className="text-xl font-bold text-slate-900">{healthSystem}</p>
            </div>

            {/* Credentials */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-slate-900 text-center">Your Login Credentials</h3>
              
              <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-slate-200">
                <Mail className="w-5 h-5 text-slate-400" />
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-blue-200">
                <Key className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Access Code</p>
                  <p className="font-mono font-bold text-lg text-blue-600 tracking-wider">{accessCode}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={copyAccessCode}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Important:</strong> Save your access code! You'll need it along with your email to log in.
              </div>
            </div>

            {/* Login Button */}
            <Link to="/hospital/login">
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                Go to Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>

            <p className="text-center text-sm text-slate-500">
              Questions?{" "}
              <a href="mailto:support@sterilemate.com" className="text-blue-600 hover:underline">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
